import { Injectable } from "@angular/core";

import { isEqual, isEmpty } from "lodash";

import { BehaviorSubject, Observable } from "rxjs";
import { distinctUntilChanged, map, shareReplay } from "rxjs/operators";


import {
  ActiveCreditGroupedByType,
  CalculationConfig,
  CalculationResult,
  CreateApplicationRequest,
  INITIAL_STATE, PersonalDataForm,
  SelectedOffice
} from "./store.model";
import {
  ApplicationOnCredit,
  ApplicationStatus
} from '@core/services/online-credit/online-credit.model';

@Injectable()

export class StoreService {

  private state = new BehaviorSubject(INITIAL_STATE);
  public value$ = this.state.asObservable();

  calculationResult$ = this.value$.pipe(
    map((value) => {
      if (!value.calculation) {
        return {} as any;
      }
      return {
        ...value.calculation,
        amountInput: value.calculation.amount.toFixed(2) || value.application.amount.toFixed(2)
      };
    }),
    shareReplay(1)
  );
  calculationConfig$ = this.value$.pipe(
    map(value => value.calculationConfig),
    shareReplay(1)
  );
  createApplicationRequest$: Observable<CreateApplicationRequest> = this.calculationResult$.pipe(
    map((value) => {
      return {
        amount: Number(value.amountInput),
        insuranceAmount: value.insurance ? +value.amountDks.toFixed(2) : 0,
        insurance: value.insurance,
        period: value.period,
        monthAmount: +value.monthlyPayment.toFixed(2),
        isOnline: this.state.value.isOnline,
        interestRate: value.interestRate
      };
    })
  );

  activeCredits$: Observable<ActiveCreditGroupedByType[]> = this.value$.pipe(
    map((value) => {
      const { application } = value;
      if (!application || !application.clientActiveCredits.length) {
        return []
      }
      const grouped: ActiveCreditGroupedByType[] = [];
      application.clientActiveCredits.forEach((credit) => {
        const groupTitle = credit.creditType.toLowerCase().includes('кредитная карта') ? 'Наличие действующих кредитных карт' : credit.creditType.toLowerCase().includes('кредит') ? 'Наличие действующих кредитов' : null;
        if (groupTitle) {
          const group = grouped.find(group => group.type === groupTitle);
          if (group) {
            group.credits.push(credit);
          } else {
            grouped.push({
              type: groupTitle,
              credits: [credit]
            });
          }
        }
      });
      return grouped;
    })
  )

  get applicationExist() {
    return !isEmpty(this.state.value.application);
  }

  get approvedApplication() {
    return this.applicationExist && this.applicationStatus === 'approved';
  }

  get value() {
    return this.state.value;
  }

  get hasPersonalData() {
    return typeof this.value.personalData !== 'undefined';
  }

  get hasUserPersonalFormData() {
    return this.hasPersonalData && typeof this.value.personalData.userFormGroup !== 'undefined';
  }

  get hasAdditionalUserPersonalFormData() {
    return this.hasPersonalData && typeof this.value.personalData.additionalFormGroup !== 'undefined';
  }

  get applicationStatus(): ApplicationStatus | undefined {
    return this.applicationExist ? this.state.value.application.status : undefined;
  }
  changeIsOnlineFlag(value: boolean) {
    const oldState = this.state.value;
    const newState = {
      ...oldState,
      isOnline: value
    };
    this.state.next(newState);
  }

  updateCalculationResult(result: CalculationResult) {
    const oldState = this.state.value;
    const newState = {
      ...oldState,
      calculation: {
        ...oldState.calculation,
        ...result
      }
    };

    this.state.next(newState);
  }

  updateApplication(application: Partial<ApplicationOnCredit>) {
    const oldState = this.state.value;
    const newState = {
      ...oldState,
      application: {
        ...oldState.application,
        ...application
      }
    };
    // Проверяем, изменилось ли состояние
    if (!isEqual(oldState.application, newState.application)) {
      this.state.next(newState);
    }
  }

  updateCalculationConfig(config: CalculationConfig) {
    const oldState = this.state.value;
    const newState = {
      ...oldState,
      calculationConfig: {
        ...oldState.calculationConfig,
        ...config
      }
    };
    this.state.next(newState);
  }

  updateSelectedOffice(office: SelectedOffice) {
    const oldState = this.state.value;
    const newState = {
      ...oldState,
      selectedOffice: {
        ...office
      }
    };
    this.state.next(newState);
  }

  updatePersonalDataForm(value: PersonalDataForm) {
    const oldState = this.state.value;
    const newState = {
      ...oldState,
      personalData: {
        ...oldState.personalData,
        ...value,
      }
    }
    this.state.next(newState);
  }

  clear() {
    this.state.next(INITIAL_STATE);
  }
}
